import React, { useState, useEffect } from 'react';
import '../styles/AdminDashboard.css';
import {
  fetchAllUsersUsage,
  fetchRecentActivitySummary,
  computeDashboardStats,
} from '../services/adminService';

// ── Small reusable stat card ─────────────────────────────────────────────────
const StatCard = ({ label, value, sub, accent }) => (
  <div className={`stat-card ${accent || ''}`}>
    <span className="stat-value">{value ?? '—'}</span>
    <span className="stat-label">{label}</span>
    {sub && <span className="stat-sub">{sub}</span>}
  </div>
);

// ── Inline bar chart ─────────────────────────────────────────────────────────
const BarChart = ({ data, colorClass }) => {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  const max = Math.max(...entries.map(([, v]) => v), 1);
  return (
    <div className="bar-chart">
      {entries.map(([label, count]) => (
        <div key={label} className="bar-row">
          <span className="bar-label">{label}</span>
          <div className="bar-track">
            <div
              className={`bar-fill ${colorClass || ''}`}
              style={{ width: `${(count / max) * 100}%` }}
            />
          </div>
          <span className="bar-count">{count}</span>
        </div>
      ))}
    </div>
  );
};

// ── 30-day sparkline ────────────────────────────────────────────────────────
const Sparkline = ({ series }) => {
  const max = Math.max(...series.map((d) => d.count), 1);
  const width = 600;
  const height = 80;
  const pad = 4;
  const step = (width - pad * 2) / (series.length - 1);

  const points = series
    .map((d, i) => {
      const x = pad + i * step;
      const y = height - pad - ((d.count / max) * (height - pad * 2));
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <div className="sparkline-wrapper">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="sparkline-svg">
        <polyline points={points} fill="none" stroke="#4f46e5" strokeWidth="2.5" strokeLinejoin="round" />
        {series.map((d, i) => {
          if (d.count === 0) return null;
          const x = pad + i * step;
          const y = height - pad - ((d.count / max) * (height - pad * 2));
          return <circle key={d.date} cx={x} cy={y} r="3" fill="#4f46e5" />;
        })}
      </svg>
      <div className="sparkline-labels">
        <span>{series[0]?.date}</span>
        <span>{series[series.length - 1]?.date}</span>
      </div>
    </div>
  );
};

// ── Main dashboard ───────────────────────────────────────────────────────────
const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefreshed, setLastRefreshed] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [usageData, activityData] = await Promise.all([
        fetchAllUsersUsage(),
        fetchRecentActivitySummary(),
      ]);
      setStats(computeDashboardStats(usageData, activityData));
      setLastRefreshed(new Date().toLocaleTimeString());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="admin-dashboard">
        <div className="admin-loading">Loading dashboard data…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-dashboard">
        <div className="admin-error">
          <p>⚠️ {error}</p>
          <button className="refresh-btn" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  const {
    totalUsers,
    activeUsersThisMonth,
    tierCounts,
    activitiesThisMonth,
    subjectCounts,
    gradeCounts,
    dailySeries,
  } = stats;

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <div className="admin-header">
        <div>
          <h2>Admin Dashboard</h2>
          <p className="admin-subtitle">Platform overview — last 30 days</p>
        </div>
        <div className="admin-header-right">
          {lastRefreshed && (
            <span className="last-refreshed">Refreshed at {lastRefreshed}</span>
          )}
          <button className="refresh-btn" onClick={load}>↻ Refresh</button>
        </div>
      </div>

      {/* Top stat cards */}
      <div className="stat-cards-grid">
        <StatCard
          label="Total Registered Users"
          value={totalUsers.toLocaleString()}
          accent="accent-blue"
        />
        <StatCard
          label="Active Users This Month"
          value={activeUsersThisMonth.toLocaleString()}
          sub={totalUsers ? `${Math.round((activeUsersThisMonth / totalUsers) * 100)}% of total` : undefined}
          accent="accent-green"
        />
        <StatCard
          label="Activities Generated (30 days)"
          value={activitiesThisMonth.toLocaleString()}
          accent="accent-indigo"
        />
        <StatCard
          label="Avg. Activities / Active User"
          value={
            activeUsersThisMonth
              ? (activitiesThisMonth / activeUsersThisMonth).toFixed(1)
              : '—'
          }
          accent="accent-amber"
        />
      </div>

      {/* Activity over time */}
      <div className="admin-section">
        <h3 className="section-title">Daily Activity — Last 30 Days</h3>
        <Sparkline series={dailySeries} />
      </div>

      {/* Charts row */}
      <div className="charts-row">
        {/* By subject */}
        <div className="admin-section half">
          <h3 className="section-title">Activities by Subject</h3>
          {Object.keys(subjectCounts).length > 0 ? (
            <BarChart data={subjectCounts} colorClass="fill-indigo" />
          ) : (
            <p className="no-data-text">No data available.</p>
          )}
        </div>

        {/* By grade */}
        <div className="admin-section half">
          <h3 className="section-title">Activities by Grade</h3>
          {Object.keys(gradeCounts).length > 0 ? (
            <BarChart data={gradeCounts} colorClass="fill-green" />
          ) : (
            <p className="no-data-text">No data available.</p>
          )}
        </div>
      </div>

      {/* Subscription tiers */}
      {Object.keys(tierCounts).length > 0 && (
        <div className="admin-section">
          <h3 className="section-title">Users by Subscription Tier</h3>
          <div className="tier-pills">
            {Object.entries(tierCounts).map(([tier, count]) => (
              <div key={tier} className={`tier-pill tier-${tier}`}>
                <span className="tier-name">{tier}</span>
                <span className="tier-count">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
